import { useState, useEffect, useCallback, useRef } from 'react';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';

// Constantes para evitar números mágicos
const FORMATO_FECHA_REGEX = /^\d{2}\/\d{2}\/\d{4}$/;
const PREFIJO_PRESTAMO = '79';
const LONGITUD_CODIGO_PRESTAMO = 10;
const PORCENTAJE_IGV = 1.18;
const HORARIO_PAGO_LABELS = {
  '07_11': '7:00:00 - 11:00:00',
  '11_15': '11:00:01 - 15:00:00',
  '15_18': '15:00:01 - 18:00:00',
  '18_19': '18:00:01 - 19:00:00',
  '19_24': '19:00:01 - 23:59:59',
};

const ReporteRentaExcel = ({
  cuotas = [],
  filters = {},
  asesores = [],
  meta = {},
  onComplete,
  onExcelGenerated,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const hasGeneratedRef = useRef(false);

  const formatDate = useCallback((dateString) => {
    try {
      if (!dateString) return '-';

      if (typeof dateString === 'string' && dateString.match(FORMATO_FECHA_REGEX)) {
        return dateString;
      }
      
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return format(date, 'dd/MM/yyyy');
    } catch {
      return '-';
    }
  }, []);

  const agregarPrefijoYCerosIzquierda = useCallback((id, prefijo, longitudTotal) => {
    const idStr = String(id);
    const cerosNecesarios = longitudTotal - (prefijo.length + idStr.length);
    return prefijo + '0'.repeat(Math.max(0, cerosNecesarios)) + idStr;
  }, []);

  const generateExcelReport = useCallback(async () => {
    if (isGenerating) return;
    setIsGenerating(true);

    const getAsesorNombre = (asesorId) => {
      if (!asesorId) return 'TODOS LOS ASESORES';
      const asesor = asesores.find(a => String(a.id) === String(asesorId));
      return asesor?.nombre_completo || `Asesor #${asesorId}`;
    };

    try {
      if (cuotas.length === 0) {
        toast.warning('No hay cuotas canceladas para generar el reporte');
        if (onComplete) onComplete();
        return;
      }

      const reportData = cuotas.map((cuota, index) => {
        const interes = parseFloat(cuota.interes || 0);
        const baseImponible = interes / PORCENTAJE_IGV;
        const montoAbonado = parseFloat(cuota.monto_abonado || 0);
        const idPrestamoFormateado = cuota.id_prestamo 
          ? agregarPrefijoYCerosIzquierda(cuota.id_prestamo, PREFIJO_PRESTAMO, LONGITUD_CODIGO_PRESTAMO)
          : '-';
        const descripcion = `PAGO DE CRÉDITO ${idPrestamoFormateado} AMORTIZANDO LA CUOTA ${cuota.nro_cuota || '-'} POR ${interes.toFixed(2)} SOLES`;
        
        return {
          'N°': index + 1,
          'ID Préstamo': idPrestamoFormateado,
          'DNI': cuota.cliente?.dni || '-',
          'Cliente': cuota.cliente?.nombre || '-',
          'N° de Cuota': cuota.nro_cuota || '-',
          'Monto Abonado': montoAbonado,
          'Monto de Cuota': parseFloat(cuota.monto_cuota || 0),
          'Interés': interes,
          'Base Imponible': baseImponible,
          'Fecha Cancelación': formatDate(cuota.fecha_cancelacion),
          'Horario de Pago': cuota.horario_pago || '-',
          'Abonado por': cuota.prestamo?.abonado_por || '-',
          'Descripción': descripcion,
        };
      });

      const totalMontoAbonado = cuotas.reduce((sum, cuota) => sum + parseFloat(cuota.monto_abonado || 0), 0);
      const totalMonto = cuotas.reduce((sum, cuota) => sum + parseFloat(cuota.monto_cuota || 0), 0);
      const totalInteres = cuotas.reduce((sum, cuota) => sum + parseFloat(cuota.interes || 0), 0);
      const totalBaseImponible = totalInteres / PORCENTAJE_IGV;

      reportData.push({
        'N°': '',
        'ID Préstamo': '',
        'DNI': '',
        'Cliente': '',
        'N° de Cuota': 'TOTALES',
        'Monto Abonado': totalMontoAbonado,
        'Monto de Cuota': totalMonto,
        'Interés': totalInteres,
        'Base Imponible': totalBaseImponible,
        'Fecha Cancelación': '',
        'Horario de Pago': '',
        'Abonado por': '',
        'Descripción': '',
      });

      const filtrosData = [
        ['REPORTE DE RENTA GENERADA'],
        [''],
        ['FILTROS APLICADOS:'],
        ['Periodo:', `${filters.fecha_inicio ? formatDate(filters.fecha_inicio) : 'No especificado'} - ${filters.fecha_fin ? formatDate(filters.fecha_fin) : 'No especificado'}`],
        ['Asesor:', getAsesorNombre(filters.advisor)],
        ['Horario de pago:', HORARIO_PAGO_LABELS[filters.horario_pago] || 'TODOS'],
        ['Abonado por:', filters.abonado_por || 'TODOS'],
        [''],
        ['RESUMEN GENERAL:'],
        ['Total de cuotas canceladas:', (meta.total_registros || 0).toString()],
        ['Total amortización (capital):', parseFloat(meta.total_general_amortizacion || 0)],
        ['Total renta (interés):', parseFloat(meta.total_general_renta || 0)],
        ['Total monto de cuotas:', parseFloat(meta.total_general_monto_cuota || 0)],
        ['Asesores activos:', (meta.asesores_activos || 0).toString()],
        ['Promedio amortización por cuota:', parseFloat(meta.promedio_amortizacion || 0)],
        ['Promedio renta por cuota:', parseFloat(meta.promedio_renta || 0)],
        ['Pagos masivos:', (meta.pagos_masivos || 0).toString()],
        ['Cuotas en pagos masivos:', (meta.cuotas_en_pagos_masivos || 0).toString()],
        ['Fecha de generación:', formatDate(new Date().toISOString())],
      ];

      const wb = XLSX.utils.book_new();
      const wsFiltros = XLSX.utils.aoa_to_sheet(filtrosData);
      wsFiltros['!cols'] = [
        { wch: 40 },
        { wch: 50 },
      ];

      ['B10', 'B11', 'B12', 'B14', 'B15'].forEach(cellRef => {
        if (wsFiltros[cellRef] && typeof wsFiltros[cellRef].v === 'number') {
          wsFiltros[cellRef].z = '#,##0.00';
          wsFiltros[cellRef].t = 'n';
        }
      });

      XLSX.utils.book_append_sheet(wb, wsFiltros, 'Resumen');
      const wsData = XLSX.utils.json_to_sheet(reportData);
      const vencimientosData = [
        ['Fecha de Vencimiento'],
        ...cuotas.map((cuota) => [formatDate(cuota.fecha_vencimiento)]),
        [''],
      ];

      const colWidths = [
        { wch: 5 },   // N°
        { wch: 12 },  // ID Préstamo
        { wch: 12 },  // DNI
        { wch: 35 },  // Cliente
        { wch: 12 },  // N° de Cuota
        { wch: 15 },  // Monto Abonado
        { wch: 15 },  // Monto de Cuota
        { wch: 15 },  // Interés
        { wch: 18 },  // Base Imponible
        { wch: 18 },  // Fecha Cancelación
        { wch: 16 },  // Horario de Pago
        { wch: 20 },  // Abonado por
        { wch: 80 },  // Descripción
        { wch: 4 },   // Separador
        { wch: 18 },  // Fecha de Vencimiento
      ];
      
      wsData['!cols'] = colWidths;
      XLSX.utils.sheet_add_aoa(wsData, vencimientosData, { origin: 'O1' });

      const totalRowNum = reportData.length + 1;
      
      for (let row = 2; row <= totalRowNum; row++) {
        const montoAbonadoCellRef = `F${row}`;
        if (wsData[montoAbonadoCellRef] && typeof wsData[montoAbonadoCellRef].v === 'number') {
          wsData[montoAbonadoCellRef].z = '#,##0.00';
          wsData[montoAbonadoCellRef].t = 'n';
        }

        const montoCellRef = `G${row}`;
        if (wsData[montoCellRef] && typeof wsData[montoCellRef].v === 'number') {
          wsData[montoCellRef].z = '#,##0.00';
          wsData[montoCellRef].t = 'n';
        }

        const interesCellRef = `H${row}`;
        if (wsData[interesCellRef] && typeof wsData[interesCellRef].v === 'number') {
          wsData[interesCellRef].z = '#,##0.00';
          wsData[interesCellRef].t = 'n';
        }

        const baseImponibleCellRef = `I${row}`;
        if (wsData[baseImponibleCellRef] && typeof wsData[baseImponibleCellRef].v === 'number') {
          wsData[baseImponibleCellRef].z = '#,##0.00';
          wsData[baseImponibleCellRef].t = 'n';
        }
      }

      const totalRowCells = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M'];
      totalRowCells.forEach(col => {
        const cellRef = col + totalRowNum;
        if (wsData[cellRef]) {
          wsData[cellRef].s = {
            font: { bold: true },
            fill: { fgColor: { rgb: "FFFFCC" } }
          };

          if ((col === 'F' || col === 'G' || col === 'H' || col === 'I') && typeof wsData[cellRef].v === 'number') {
            wsData[cellRef].z = '#,##0.00';
            wsData[cellRef].t = 'n';
          }
        }
      });

      XLSX.utils.book_append_sheet(wb, wsData, 'Detalle de Cuotas');

      const excelBuffer = XLSX.write(wb, { 
        bookType: 'xlsx', 
        type: 'array',
        cellStyles: true 
      });

      const blob = new Blob([excelBuffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const url = URL.createObjectURL(blob);

      if (onExcelGenerated) {
        onExcelGenerated(url);
      }

      toast.success(`Reporte Excel generado correctamente. ${cuotas.length} cuotas procesadas.`);

    } catch (error) {
      console.error('Error generando Excel:', error);
      toast.error('Error al generar el reporte Excel: ' + error.message);
    } finally {
      setIsGenerating(false);
      if (onComplete) {
        onComplete();
      }
    }
  }, [cuotas, filters, asesores, meta, isGenerating, onComplete, onExcelGenerated, formatDate, agregarPrefijoYCerosIzquierda]);

  useEffect(() => {
    if (cuotas && cuotas.length > 0 && !isGenerating && !hasGeneratedRef.current) {
      hasGeneratedRef.current = true;
      generateExcelReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cuotas]);

  return (
    <div className="hidden">
      {isGenerating && (
        <div className="fixed top-4 right-4 bg-blue-500 text-white p-3 rounded-lg shadow-lg z-50">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            Generando reporte Excel...
          </div>
        </div>
      )}
    </div>
  );
};

export default ReporteRentaExcel;
